<template>
  <div
      class="verification-recorder"
      :class="{ 'verification-recorder--native': recorder.isNative }">
    <div class="verification-recorder__preview">
      <video
          v-if="!recorder.isNative"
          ref="previewVideo"
          class="verification-recorder__video"
          playsinline
          muted/>

      <div class="verification-recorder__overlay">
        <div class="verification-recorder__toolbar">
          <button
              v-if="!isRecording"
              type="button"
              @click="emit('cancel')"
              class="verification-recorder__chip">
            Back
          </button>
          <div v-else class="verification-recorder__toolbar-spacer"/>

          <button
              v-if="!isRecording"
              type="button"
              @click="flipCamera"
              class="verification-recorder__chip">
            Flip
          </button>
        </div>

        <div class="verification-recorder__prompts">
          <div class="verification-recorder__phrase">
            <div class="verification-recorder__phrase-label">Say this phrase clearly</div>
            <div class="verification-recorder__phrase-value">"{{ phrase }}"</div>
          </div>

          <div
              v-if="coupleAccount"
              class="verification-recorder__notice">
            Both of you must be visible in the video.
          </div>
        </div>
      </div>

      <div
          v-if="isRecording"
          class="verification-recorder__timer">
        <span class="verification-recorder__timer-dot"/>
        <span class="verification-recorder__timer-text">{{ formattedDuration }}</span>
      </div>
    </div>

    <div class="verification-recorder__controls">
      <button
          v-if="!isRecording"
          type="button"
          @click="startRecording"
          :disabled="initializing || busy"
          class="verification-recorder__record-btn verification-recorder__record-btn--start">
        <span class="verification-recorder__record-btn-inner"/>
      </button>

      <button
          v-else
          type="button"
          @click="stopRecording"
          :disabled="busy || durationSeconds < 2"
          class="verification-recorder__record-btn verification-recorder__record-btn--stop">
        <span class="verification-recorder__stop-icon"/>
      </button>

      <p class="verification-recorder__hint">
        <span v-if="!isRecording">Tap the button when you're ready to record.</span>
        <span v-else-if="durationSeconds < 2">Keep recording…</span>
        <span v-else>Tap the square to finish recording.</span>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import {
  useVerificationVideoRecorder,
  VERIFICATION_PHRASE,
} from '@/composables/useVerificationVideoRecorder';

const props = defineProps<{
  coupleAccount?: boolean;
  phrase?: string;
}>();

const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'recorded', blob: Blob): void;
  (event: 'error', message: string): void;
}>();

const recorder = useVerificationVideoRecorder();
const previewVideo = ref<HTMLVideoElement | null>(null);
const initializing = ref(true);
const busy = ref(false);
const isRecording = ref(false);
const durationSeconds = ref(0);

let timerId: ReturnType<typeof setInterval> | null = null;
const maxDurationSeconds = 60;

const phrase = computed(() => props.phrase || VERIFICATION_PHRASE);

const formattedDuration = computed(() => {
  const minutes = Math.floor(durationSeconds.value / 60).toString().padStart(2, '0');
  const seconds = (durationSeconds.value % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
});

const clearTimer = () => {
  if (timerId) {
    clearInterval(timerId);
    timerId = null;
  }
};

const setupPreview = async () => {
  initializing.value = true;

  try {
    await recorder.initializePreview();
    if (previewVideo.value) {
      await recorder.attachToVideoElement(previewVideo.value);
    }
  } catch {
    emit('error', 'Camera and microphone access is required to record your verification video.');
  } finally {
    initializing.value = false;
  }
};

const startTimer = () => {
  clearTimer();
  durationSeconds.value = 0;
  timerId = setInterval(() => {
    durationSeconds.value += 1;
    if (durationSeconds.value >= maxDurationSeconds) {
      stopRecording();
    }
  }, 1000);
};

const startRecording = async () => {
  if (initializing.value || busy.value || isRecording.value) {
    return;
  }

  try {
    busy.value = true;
    await recorder.startRecording();
    isRecording.value = true;
    startTimer();
  } catch {
    emit('error', 'Unable to start recording. Please try again.');
  } finally {
    busy.value = false;
  }
};

const stopRecording = async () => {
  if (!isRecording.value || busy.value) {
    return;
  }

  try {
    busy.value = true;
    clearTimer();
    const blob = await recorder.stopRecording();
    isRecording.value = false;
    emit('recorded', blob);
  } catch {
    emit('error', 'Unable to finish recording. Please try again.');
  } finally {
    busy.value = false;
  }
};

const flipCamera = async () => {
  if (isRecording.value || initializing.value) {
    return;
  }

  try {
    await recorder.flipCamera();
    if (previewVideo.value) {
      await recorder.attachToVideoElement(previewVideo.value);
    }
  } catch {
    emit('error', 'Unable to switch camera.');
  }
};

onMounted(setupPreview);

onBeforeUnmount(async () => {
  clearTimer();
  await recorder.destroy();
});
</script>

<style scoped>
.verification-recorder {
  box-sizing: border-box;
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  max-height: 100%;
  flex-direction: column;
  gap: 0.75rem;
}

.verification-recorder__preview {
  position: relative;
  display: flex;
  overflow: hidden;
  width: 100%;
  min-height: 0;
  flex: 1 1 auto;
  max-height: calc(100% - 7.5rem);
  aspect-ratio: 3 / 4;
  border-radius: 1rem;
  background: #1A1B22;
}

.verification-recorder__video {
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
  object-fit: cover;
}

.verification-recorder__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  pointer-events: none;
}

.verification-recorder__toolbar {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  pointer-events: auto;
}

.verification-recorder__toolbar-spacer {
  width: 4rem;
}

.verification-recorder__chip {
  border-radius: 9999px;
  background: rgb(26 27 34 / 0.72);
  padding: 0.5rem 0.875rem;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 500;
}

.verification-recorder__prompts {
  display: flex;
  margin-top: auto;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem;
}

.verification-recorder__phrase {
  border-radius: 1rem;
  border: 1px solid rgb(235 255 0 / 0.4);
  background: rgb(26 27 34 / 0.78);
  padding: 0.75rem;
  text-align: center;
  backdrop-filter: blur(8px);
}

.verification-recorder__phrase-label {
  color: #A5A7AF;
  font-size: 0.6875rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.verification-recorder__phrase-value {
  margin-top: 0.375rem;
  color: #EBFF00;
  font-size: clamp(0.95rem, 3vw, 1.15rem);
  font-weight: 600;
  line-height: 1.3;
}

.verification-recorder__notice {
  border-radius: 0.75rem;
  border: 1px solid #EBFF00;
  background: rgb(235 255 0 / 0.1);
  padding: 0.625rem 0.75rem;
  color: #EBFF00;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.35;
  text-align: center;
}

.verification-recorder__timer {
  position: absolute;
  top: 0.75rem;
  left: 50%;
  display: flex;
  transform: translateX(-50%);
  align-items: center;
  gap: 0.5rem;
  border-radius: 9999px;
  background: rgb(220 38 38 / 0.9);
  padding: 0.375rem 0.75rem;
}

.verification-recorder__timer-dot {
  height: 0.625rem;
  width: 0.625rem;
  animation: pulse 1.5s ease-in-out infinite;
  border-radius: 9999px;
  background: #fff;
}

.verification-recorder__timer-text {
  color: #fff;
  font-size: 0.875rem;
  font-weight: 500;
}

.verification-recorder__controls {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: center;
  gap: 0.625rem;
  padding-bottom: 0.25rem;
}

.verification-recorder__record-btn {
  display: flex;
  height: 4rem;
  width: 4rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
}

.verification-recorder__record-btn:disabled {
  opacity: 0.4;
}

.verification-recorder__record-btn--start {
  border: 4px solid #EBFF00;
  background: rgb(235 255 0 / 0.2);
}

.verification-recorder__record-btn-inner {
  height: 2.5rem;
  width: 2.5rem;
  border-radius: 9999px;
  background: #EBFF00;
}

.verification-recorder__record-btn--stop {
  border: 4px solid #ef4444;
  background: rgb(239 68 68 / 0.2);
}

.verification-recorder__stop-icon {
  height: 2rem;
  width: 2rem;
  border-radius: 0.375rem;
  background: #ef4444;
}

.verification-recorder__hint {
  margin: 0;
  max-width: 16rem;
  padding-inline: 1rem;
  color: #A5A7AF;
  font-size: 0.8125rem;
  line-height: 1.4;
  text-align: center;
}

@media (min-width: 768px) {
  .verification-recorder {
    gap: 0.625rem;
  }

  .verification-recorder__preview {
    max-height: calc(100% - 6.75rem);
  }

  .verification-recorder__phrase-value {
    font-size: 1rem;
  }

  .verification-recorder__controls {
    gap: 0.5rem;
  }

  .verification-recorder__record-btn {
    height: 3.5rem;
    width: 3.5rem;
  }

  .verification-recorder__record-btn-inner {
    height: 2rem;
    width: 2rem;
  }

  .verification-recorder__stop-icon {
    height: 1.5rem;
    width: 1.5rem;
  }
}

@media (max-height: 760px) {
  .verification-recorder__preview {
    max-height: calc(100% - 6.25rem);
  }

  .verification-recorder__prompts {
    gap: 0.5rem;
    padding: 0.625rem;
  }

  .verification-recorder__phrase {
    padding: 0.625rem;
  }

  .verification-recorder__controls {
    gap: 0.375rem;
  }
}

.verification-recorder--native .verification-recorder__preview {
  background: transparent;
}
</style>
